import {
  BasePaginateParamsSchema,
  BasePaginateResultSchema,
} from '@/api/common.ts'
import dayjs from 'dayjs'
import weekOfYear from 'dayjs/plugin/weekOfYear'
import { z } from 'zod'
dayjs.extend(weekOfYear)

export const Prd1010SearchParamsSchema = z
  .object({
    number: z.string().optional(),
    lotNo: z.string().optional(),
    partyId: z.string().optional(),
    itemId: z.string().optional(),
    status: z
      .union([z.literal('1'), z.literal('2'), z.literal('3'), z.literal('4')])
      .optional(),
    mfgDate: z
      .string()
      .optional()
      .transform((val) => {
        if (val === undefined) return dayjs().toDate()
        if (val === 'null') return null
        return new Date(val)
      })
      .nullable(),
    machine: z.string().optional(),
    onlyNotCompleted: z.coerce.boolean().optional(),
  })
  .merge(BasePaginateParamsSchema)
  .transform((values) => ({
    ...values,
    mfgYear: values.mfgDate ? dayjs(values.mfgDate).isoWeekYear() : null,
    mfgWeek: values.mfgDate ? dayjs(values.mfgDate).isoWeek() : null,
  }))

export type Prd1010SearchParams = z.infer<typeof Prd1010SearchParamsSchema>

const WorkOrderMfgSchema = z.object({
  id: z.number(),
  shift: z.number(),
  date: z.coerce.date(),
  pumpQtyOutput: z.number(),
  pumpQtyNgWaiting: z.number(),
  pumpQtyNgSortPassed: z.number(),
  pumpQtyNgSortNg: z.number(),
  pumpQtyUnFinished: z.number(),
  pumpHoldQty: z.number(),
  pumpHoldPassed: z.number(),
  pumpHoldNg: z.number(),
  tubeQtyOutput: z.number(),
  tubeQtyUnFinished: z.number(),
  tubePumpNgQty: z.number(),
  tubeNgQty: z.number(),
  tubeHoldQty: z.number(),
  tubeHoldPassed: z.number(),
  tubeHoldNg: z.number(),
})
export type Prd1010WorkOrderMfgModel = z.infer<typeof WorkOrderMfgSchema>

export const Prd1010SearchResultModelSchema = z.object({
  id: z.number(),
  deliveryDate: z.coerce.date(),
  deliveryWeekNo: z.number().int(),
  productionDate: z.coerce.date(),
  productionWeekNo: z.number().int(),
  partyId: z.number(),
  partyName: z.string(),
  itemId: z.number(),
  itemCode: z.string(),
  itemName: z.string(),
  qty: z.number(),
  number: z.string(),
  lotNo: z.string().nullable(),
  productionType: z.string().nullable(),
  machine: z.string().nullable(),
  startDate: z.coerce.date().nullable(),
  endDate: z.coerce.date().nullable(),
  mfgs: z.array(WorkOrderMfgSchema),
  hasUsedMaterials: z.boolean(),
})

export type Prd1010SearchResultModel = z.infer<
  typeof Prd1010SearchResultModelSchema
>

export const Prd1010SearchResultSchema = z
  .object({
    data: z.array(Prd1010SearchResultModelSchema),
  })
  .merge(BasePaginateResultSchema)

export type Prd1010SearchResult = z.infer<typeof Prd1010SearchResultSchema>

export const Prd1010ItemModelSchema = z.object({
  id: z.number(),
  deliveryDate: z.coerce.date(),
  deliveryWeekNo: z.number().int(),
  productionDate: z.coerce.date(),
  productionWeekNo: z.number().int(),
  partyId: z.number(),
  partyName: z.string(),
  itemId: z.number(),
  itemCode: z.string(),
  itemName: z.string(),
  qty: z.number(),
  number: z.string(),
  lotNo: z.string().nullable(),
  productionType: z.string().nullable(),
  machine: z.string().nullable(),
  startDate: z.coerce.date().nullable(),
  endDate: z.coerce.date().nullable(),
  mfgId: z.number().optional(),
  shift: z.number(),
  date: z.coerce.date().optional(),
  pumpQtyOutput: z.number().optional(),
  pumpQtyNgWaiting: z.number().optional(),
  pumpQtyNgSortPassed: z.number().optional(),
  pumpQtyNgSortNg: z.number().optional(),
  pumpQtyUnFinished: z.number().optional(),
  pumpHoldQty: z.number().optional(),
  pumpHoldPassed: z.number().optional(),
  pumpHoldNg: z.number().optional(),
  tubeQtyOutput: z.number().optional(),
  tubeQtyUnFinished: z.number().optional(),
  tubePumpNgQty: z.number().optional(),
  tubeNgQty: z.number().optional(),
  tubeHoldQty: z.number().optional(),
  tubeHoldPassed: z.number().optional(),
  tubeHoldNg: z.number().optional(),
  hasUsedMaterials: z.boolean(),
})

export type Prd1010ItemModel = z.infer<typeof Prd1010ItemModelSchema> & {
  subRows?: Prd1010ItemModel[]
}
